import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, formatDate } from "@/lib/utils-app";
import { toast } from "sonner";
import Sparkline from "@/components/Sparkline";
import SuspendUserDialog from "@/components/SuspendUserDialog";
import AdminUserDetailsDialog from "@/components/AdminUserDetailsDialog";
import { ChartBarIcon as ChartBar, UsersThreeIcon as UsersThree, TableIcon as TableIcon, DownloadSimpleIcon as DownloadSimple, CurrencyDollarIcon as CurrencyDollar, DogIcon as Dog, PawPrintIcon as PawPrint, UsersFourIcon as UsersFour, ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, CrownIcon as Crown, ShieldSlashIcon as ShieldSlash, GraduationCapIcon as GraduationCap, IdentificationCardIcon as IdentificationCard, FileArrowDownIcon as FileArrowDown } from "@phosphor-icons/react";

const BROWSE_COLLECTIONS = [
  "users",
  "dogs",
  "litters",
  "breeders",
  "transfers",
  "payment_transactions",
  "breeding_listings",
  "events",
  "certificate_purchases",
];

// Cycling tone palette for per-kind sparklines — picks up the brand red,
// deep charcoal, gold, and a muted slate so the lines stay legible against
// white cards.
const KIND_TONE = [
  { stroke: "#C1272D", fill: "rgba(193, 39, 45, 0.12)" },
  { stroke: "#0E0E0E", fill: "rgba(14, 14, 14, 0.10)" },
  { stroke: "#C9A227", fill: "rgba(201, 162, 39, 0.15)" },
  { stroke: "#5A6B56", fill: "rgba(90, 107, 86, 0.12)" },
];

const VALID_TABS = ["analytics", "users", "browse", "export"];

export default function AdminData({ defaultTab = "analytics" } = {}) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initialTab = VALID_TABS.includes(urlTab) ? urlTab : defaultTab;
  const [tab, setTab] = useState(initialTab);

  // Keep tab + URL in sync (back/forward + direct deep-links).
  useEffect(() => {
    if (VALID_TABS.includes(urlTab) && urlTab !== tab) {
      setTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  const handleTabChange = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === defaultTab) {
      params.delete("tab");
    } else {
      params.set("tab", next);
    }
    setSearchParams(params, { replace: true });
  };

  if (user && user.role !== "admin") {
    return (
      <AppShell>
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center">
          <h1 className="font-spectral text-3xl mb-2">Admin only</h1>
          <p className="text-[#6B6B6B]">This page is restricted to registry administrators.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Admin</p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight" data-testid="admin-data-heading">
            Data &amp; Operations
          </h1>
          <p className="text-[#6B6B6B] mt-2">
            Analytics, user management, raw collection browsing, and CSV exports —
            everything you need to run the registry from one place.
          </p>
        </div>

        <Tabs value={tab} onValueChange={handleTabChange}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border border-[#E5E5E5] rounded-full p-1">
              <TabsTrigger value="analytics" className="rounded-full" data-testid="admin-data-tab-analytics">
                <ChartBar size={14} weight="duotone" className="mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-full" data-testid="admin-data-tab-users">
                <UsersThree size={14} weight="duotone" className="mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="browse" className="rounded-full" data-testid="admin-data-tab-browse">
                <TableIcon size={14} weight="duotone" className="mr-2" />
                Browse
              </TabsTrigger>
              <TabsTrigger value="export" className="rounded-full" data-testid="admin-data-tab-export">
                <DownloadSimple size={14} weight="duotone" className="mr-2" />
                Export
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsPanel />
          </TabsContent>
          <TabsContent value="users" className="mt-6">
            <UsersPanel selfId={user?.id} />
          </TabsContent>
          <TabsContent value="browse" className="mt-6">
            <BrowsePanel />
          </TabsContent>
          <TabsContent value="export" className="mt-6">
            <ExportPanel />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/data/analytics")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/admin/data/activation-funnel")
      .then((r) => setFunnel(r.data))
      .catch(() => setFunnel(null));
  }, []);

  if (loading) return <div className="text-[#6B6B6B]">Loading…</div>;
  if (!data) return <div className="text-[#6B6B6B]">No data.</div>;

  const kpis = [
    { label: "Revenue (all time)", value: `$${data.revenue.all_time_usd.toLocaleString()}`, icon: CurrencyDollar, tone: "gold" },
    { label: "Revenue (30d)", value: `$${data.revenue.last_30d_usd.toLocaleString()}`, icon: CurrencyDollar, tone: "gold" },
    { label: "Users", value: data.totals.users, icon: UsersThree, sub: `${data.totals.admins} admin · ${data.totals.suspended_users} suspended` },
    { label: "Dogs", value: data.totals.dogs, icon: Dog },
    { label: "Litters", value: data.totals.litters, icon: PawPrint },
    { label: "Breeders", value: data.totals.breeders, icon: UsersFour },
    { label: "Transfers", value: data.totals.transfers, icon: ArrowsLeftRight, sub: `${data.totals.approved_transfers} approved` },
    { label: "Active subscriptions", value: data.totals.active_subscriptions, icon: Crown },
  ];

  return (
    <div className="space-y-6" data-testid="admin-data-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div
            key={k.label}
            className={`bg-white border rounded-xl p-5 ${k.tone === "gold" ? "border-[#E5D08A] bg-[#FBF7EC]" : "border-[#E5E5E5]"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <k.icon size={16} weight="duotone" className={k.tone === "gold" ? "text-[#C9A227]" : "text-[#6B6B6B]"} />
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">{k.label}</p>
            </div>
            <p className="font-spectral text-3xl tracking-tight">{k.value}</p>
            {k.sub && <p className="text-[11px] text-[#6B6B6B] mt-1">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* 30-day revenue timeseries — overall plus top-4 kinds */}
      <div
        className="bg-white border border-[#E5E5E5] rounded-xl p-5"
        data-testid="admin-revenue-timeseries"
      >
        <div className="flex items-center justify-between mb-1">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Revenue — last 30 days
            </p>
            <p className="font-spectral text-3xl tracking-tight mt-1">
              ${data.revenue.last_30d_usd.toLocaleString()}
              <span className="text-sm text-[#6B6B6B] ml-2 font-manrope">
                · {data.revenue.transactions_30d} tx
              </span>
            </p>
          </div>
          <div className="text-right text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
            <div>{data.revenue.daily_30d[0]?.date}</div>
            <div className="text-ink">→ {data.revenue.daily_30d[data.revenue.daily_30d.length - 1]?.date}</div>
          </div>
        </div>
        <Sparkline data={data.revenue.daily_30d} height={72} />
        {data.revenue.daily_30d_by_kind.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#E5E5E5]">
            {data.revenue.daily_30d_by_kind.map((k, idx) => {
              const kindTotal = k.series.reduce((s, p) => s + (p.total || 0), 0);
              return (
                <div key={k.kind} data-testid={`admin-revenue-spark-${k.kind}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      {k.kind}
                    </span>
                    <span className="text-xs font-mono text-ink">
                      ${kindTotal.toLocaleString()}
                    </span>
                  </div>
                  <Sparkline
                    data={k.series}
                    height={36}
                    strokeColor={KIND_TONE[idx % KIND_TONE.length].stroke}
                    fillColor={KIND_TONE[idx % KIND_TONE.length].fill}
                    showPeak={false}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TourStatsCard />

      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-3">New this week</p>
          <div className="space-y-2 text-sm">
            <Row label="New users (7d)" value={data.recent.new_users_7d} />
            <Row label="New dogs (7d)" value={data.recent.new_dogs_7d} />
            <Row label="New users (30d)" value={data.recent.new_users_30d} />
            <Row label="New dogs (30d)" value={data.recent.new_dogs_30d} />
          </div>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-3">Revenue by kind</p>
          {data.revenue.by_kind.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">No finalized transactions yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.revenue.by_kind.slice(0, 8).map((r) => (
                <li key={r.kind} className="flex justify-between">
                  <span className="truncate">{r.kind}</span>
                  <span className="font-mono">${r.total.toLocaleString()} <span className="text-[#6B6B6B]">({r.count})</span></span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-3">Top breeders by dog count</p>
          {data.top_breeders.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">No breeders yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.top_breeders.map((b) => (
                <li key={b.id} className="flex justify-between gap-2">
                  <span className="truncate">{b.name}</span>
                  <span className="font-mono text-crimson">{b.dog_count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {data.plan_distribution.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-3">Active plan distribution</p>
          <div className="flex flex-wrap gap-2">
            {data.plan_distribution.map((p) => (
              <Badge key={p.plan} variant="outline" className="text-xs">
                {p.plan}: {p.count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {funnel && funnel.rows.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="activation-funnel-card">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1">
            Signup → activation by persona
          </p>
          <p className="text-[11px] text-[#9A9A9A] mb-3">
            "Activated" = took their first meaningful action (registered a dog, applied as a pro,
            opened a store…). Overall: {funnel.activated}/{funnel.total} ({funnel.total ? Math.round((funnel.activated / funnel.total) * 100) : 0}%).
            Stalled users get automatic day-2 and day-7 nudges.
          </p>
          <div className="space-y-2">
            {funnel.rows.map((r) => (
              <div key={r.persona} className="flex items-center gap-3" data-testid="activation-funnel-row">
                <span className="w-40 flex-shrink-0 text-[12px] font-semibold capitalize">{r.persona.replace(/_/g, " ")}</span>
                <div className="flex-1 h-3 rounded-full bg-[#F1F1F1] overflow-hidden">
                  <div
                    className={`h-full rounded-full ${r.rate >= 60 ? "bg-emerald-500" : r.rate >= 30 ? "bg-[#C9A227]" : "bg-crimson"}`}
                    style={{ width: `${Math.max(r.rate, 3)}%` }}
                  />
                </div>
                <span className="w-24 flex-shrink-0 text-right text-[12px] font-mono">
                  {r.activated}/{r.total} · {r.rate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-[#6B6B6B] text-right font-mono">
        Generated {formatDate(data.generated_at)}
      </p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#6B6B6B]">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tour-completion analytics — separate API call so it doesn't bloat the
// /analytics endpoint payload, and so it can be retried independently if the
// underlying user query is slow.
// ---------------------------------------------------------------------------
function TourStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/data/tour-stats")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Reshape the daily series for the existing Sparkline (it uses `total`).
  const sparkData = (data?.last_30d?.daily_series || []).map((p) => ({
    date: p.date,
    total: p.count,
  }));

  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-xl p-5"
      data-testid="admin-tour-stats"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] flex items-center gap-1.5">
            <GraduationCap size={12} weight="duotone" className="text-gold" />
            Onboarding tour
          </p>
          <p className="font-spectral text-3xl tracking-tight mt-1">
            {data ? `${data.completion_rate_percent}%` : "—"}
            <span className="text-sm text-[#6B6B6B] ml-2 font-manrope">
              {data ? `· ${data.completed_users.toLocaleString()} of ${data.eligible_users.toLocaleString()} users` : ""}
            </span>
          </p>
        </div>
        <div className="text-right text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
          <div>Last 30 days</div>
          <div className="text-ink">
            {data ? `${data.last_30d.completion_rate_percent}% (${data.last_30d.completed} / ${data.last_30d.new_users})` : "—"}
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-xs text-[#6B6B6B]" data-testid="admin-tour-stats-loading">
          Loading completion data…
        </div>
      )}
      {error && !loading && (
        <div className="text-xs text-[#991B1B]" data-testid="admin-tour-stats-error">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <Sparkline
            data={sparkData}
            height={56}
            strokeColor="#C9A227"
            fillColor="#FBF7EC"
            showPeak
          />
          <div className="mt-4 pt-4 border-t border-[#E5E5E5] grid grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">Eligible</p>
              <p className="font-mono text-base text-ink mt-0.5">{data.eligible_users.toLocaleString()}</p>
              <p className="text-[10px] text-[#6B6B6B]">non-admin, not suspended</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">Completed</p>
              <p className="font-mono text-base text-ink mt-0.5">{data.completed_users.toLocaleString()}</p>
              <p className="text-[10px] text-[#6B6B6B]">finished or dismissed the tour</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">Last 30d new</p>
              <p className="font-mono text-base text-ink mt-0.5">{data.last_30d.new_users.toLocaleString()}</p>
              <p className="text-[10px] text-[#6B6B6B]">{data.last_30d.completed} of those finished</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
function userPulse(u) {
  const days = (iso) => iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null;
  const seen = days(u.last_login_at);
  const src = u.signup_source || {};
  const via = src.utm_source || (src.referrer ? (() => { try { return new URL(src.referrer).hostname.replace("www.", ""); } catch { return "link"; } })() : (u.signup_platform && u.signup_platform !== "web" ? `${u.signup_platform} app` : ""));
  const seenLabel = seen == null ? "never logged back in" : seen === 0 ? "seen today" : `seen ${seen}d ago${seen >= 14 ? " ⚠" : ""}`;
  return via ? `${seenLabel} · via ${via}` : seenLabel;
}

function UsersPanel({ selfId }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [detailTargetId, setDetailTargetId] = useState(null);
  const [filter, setFilter] = useState(() => {
    const f = new URLSearchParams(window.location.search).get("filter");
    return ["suspended", "restricted", "quiet", "deletion", "deleted", "never", "nopersona", "lapsed"].includes(f) ? f : "all";
  });
  const [personaFilter, setPersonaFilter] = useState("");
  const limit = 50;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data/users", {
        params: {
          q: q || undefined, limit, offset,
          suspended: filter === "suspended" || undefined,
          restricted: filter === "restricted" || undefined,
          deletion: filter === "deletion" || undefined,
          deleted: filter === "deleted" || undefined,
          never: filter === "never" || undefined,
          nopersona: filter === "nopersona" || undefined,
          lapsed: filter === "lapsed" || undefined,
          quiet: filter === "quiet" ? 14 : undefined,
          persona: personaFilter || undefined,
        },
      });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, filter, personaFilter]);

  const toggleRole = async (u) => {
    const nextRole = u.role === "admin" ? "user" : "admin";
    try {
      await api.post(`/admin/data/users/${u.id}/role`, { role: nextRole });
      toast.success(`${u.email} is now ${nextRole}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSuspend = (u) => setSuspendTarget(u);

  const purgeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThe account, login and personal data are wiped immediately. Registry records (dogs, transfers) survive for lineage integrity. This cannot be undone.`)) return;
    try {
      await api.post(`/admin/users-ops/purge/${u.id}`);
      toast.success("Account permanently deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const completeDeletion = async (u) => {
    if (!window.confirm(`Permanently finalize ${u.email}'s deletion request NOW (skipping the remaining grace window)?\n\nTheir identity, login, contact info, sessions and inbox are wiped. Registry records (dogs, pedigrees, payments) are kept but anonymized. This cannot be undone.`)) return;
    if (!window.confirm("Last check — this is irreversible. Proceed?")) return;
    try {
      await api.post(`/admin/users-ops/complete-deletion/${u.id}`);
      toast.success("Account deleted and anonymized.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-data-users">
      {/* Workflow hint — how to add a new admin */}
      <div className="bg-gradient-to-br from-[#FBF7EC] to-white border border-[#C9A227]/30 rounded-xl p-4 text-sm" data-testid="admin-users-workflow-hint">
        <div className="flex items-start gap-3">
          <Crown size={18} weight="fill" className="text-[#C9A227] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-ink mb-1">How to add a new admin</p>
            <ol className="text-[#6B6B6B] space-y-0.5 list-decimal list-inside">
              <li>Have the new admin <a href="/register" className="text-[#C1272D] underline">register a normal account</a> with their email.</li>
              <li>Find them in the table below (search by name or email).</li>
              <li>Click <span className="font-medium text-ink">Promote</span> → confirm. They&rsquo;ll have full admin access on their next login.</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input
          placeholder="Search by name or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setOffset(0); load(); } }}
          className="max-w-sm rounded-lg"
          data-testid="admin-users-search"
        />
        <Button onClick={() => { setOffset(0); load(); }} className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
          Search
        </Button>
        <span className="ml-auto text-sm text-[#6B6B6B]">Total: <span className="font-mono">{total}</span></span>
      </div>

      {/* Command-center filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-users-filters">
        {[
          ["all", "All"],
          ["suspended", "Suspended"],
          ["restricted", "Chat-restricted"],
          ["quiet", "Gone quiet 14+ d"],
          ["never", "Never returned"],
          ["nopersona", "No persona"],
          ["lapsed", "Lapsed plans"],
          ["deletion", "Deletion requests"],
          ["deleted", "Deleted"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => { setOffset(0); setFilter(k); }}
            className={`text-[11px] font-bold uppercase tracking-[0.08em] rounded-full px-3 py-1.5 border transition-colors ${
              filter === k ? "bg-[#0E0E0E] text-white border-[#0E0E0E]" : "bg-white text-[#6B6B6B] border-[#DDD] hover:border-ink"
            }`}
            data-testid={`admin-users-filter-${k}`}
          >
            {label}
          </button>
        ))}
        <select
          value={personaFilter}
          onChange={(e) => { setOffset(0); setPersonaFilter(e.target.value); }}
          className="text-[11px] font-bold uppercase tracking-[0.08em] rounded-full px-3 py-1.5 border border-[#DDD] bg-white text-[#6B6B6B]"
          data-testid="admin-users-persona-filter"
        >
          <option value="">Any persona / hat</option>
          <option value="breeder">Breeder</option>
          <option value="dog_owner">Dog owner</option>
          <option value="buyer">Buyer</option>
          <option value="transporter">Transporter</option>
          <option value="international_transporter">Intl courier</option>
          <option value="service_provider">Service pro</option>
          <option value="storefront_seller">Storefront</option>
        </select>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-10 text-center text-[#6B6B6B]">
          No users match.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B]">
              <tr>
                <th className="text-left p-3 font-bold">User</th>
                <th className="text-left p-3 font-bold">Role</th>
                <th className="text-left p-3 font-bold">Plan</th>
                <th className="text-right p-3 font-bold">Dogs</th>
                <th className="text-left p-3 font-bold">Joined</th>
                <th className="text-right p-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.id}
                  className={`border-t border-[#E5E5E5] ${u.suspended ? "bg-[#C1272D]/5" : "hover:bg-[#FAFAFA]"}`}
                  data-testid={`admin-user-row-${u.id}`}
                >
                  <td className="p-3">
                    <div className="font-medium">{u.name || "—"}</div>
                    <div className="text-xs text-[#6B6B6B] font-mono">{u.email}</div>
                    <div className="text-[10px] text-[#9CA3AF] mt-0.5" data-testid={`admin-user-pulse-${u.id}`}>
                      {userPulse(u)}
                    </div>
                    {(u.persona || (u.hats || []).length > 0) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {[u.persona, ...(u.hats || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).map((p) => (
                          <span key={p} className="text-[9px] font-bold uppercase tracking-wider bg-[#FBF7EC] text-[#8A6A00] border border-[#E8D9A6] rounded-full px-1.5 py-0.5">
                            {String(p).replaceAll("_", " ")}
                          </span>
                        ))}
                      </div>
                    )}
                    {u.suspended && u.suspension_reason && (
                      <div
                        className="mt-1 text-[11px] text-crimson bg-[#C1272D]/5 border border-[#C1272D]/20 rounded px-2 py-1 max-w-md"
                        data-testid={`admin-user-suspension-reason-${u.id}`}
                      >
                        <span className="font-semibold">Suspended:</span> {u.suspension_reason}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {u.role === "admin" ? (
                      <Badge className="bg-[#C9A227]/20 text-[#8A6A00] border-[#C9A227]/40 hover:bg-[#C9A227]/20">
                        <Crown size={10} weight="fill" className="mr-1" /> admin
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">user</Badge>
                    )}
                    {u.suspended && (
                      <Badge className="ml-1 bg-[#C1272D]/10 text-crimson border-[#C1272D]/30 hover:bg-[#C1272D]/10">
                        suspended
                      </Badge>
                    )}
                    {u.chat_moderation?.status === "restricted" && (
                      <Badge className="ml-1 bg-[#C1272D]/10 text-crimson border-[#C1272D]/30 hover:bg-[#C1272D]/10" data-testid={`admin-user-chat-restricted-${u.id}`}>
                        chat restricted
                      </Badge>
                    )}
                    {u.chat_moderation?.status === "warned" && (
                      <Badge className="ml-1 bg-[#C9A227]/15 text-[#8A6A00] border-[#C9A227]/40 hover:bg-[#C9A227]/15">
                        warned
                      </Badge>
                    )}
                    {u.account_deleted ? (
                      <Badge className="ml-1 bg-[#EEE] text-[#6B6B6B] border-[#DDD] hover:bg-[#EEE]">deleted</Badge>
                    ) : u.deletion_requested_at ? (
                      <Badge className="ml-1 bg-[#C1272D] text-white hover:bg-[#C1272D]" data-testid={`admin-user-deletion-badge-${u.id}`}>
                        deletion requested{u.deletion_purge_at ? ` · purges ${new Date(u.deletion_purge_at).toLocaleDateString()}` : ""}
                      </Badge>
                    ) : null}
                  </td>
                  <td className="p-3 text-xs">
                    {u.plan ? (
                      <>
                        <div>{u.plan}</div>
                        <div className="text-[11px] text-[#6B6B6B]">
                          exp {formatDate(u.plan_expires_at)}
                        </div>
                      </>
                    ) : (
                      <span className="text-[#6B6B6B]">free</span>
                    )}
                    {u.lifetime_spend_usd > 0 && (
                      <div className="text-[11px] text-emerald-700 font-semibold" data-testid="user-lifetime-spend">
                        ${u.lifetime_spend_usd.toFixed(2)} lifetime · {u.paid_purchases} purchase{u.paid_purchases === 1 ? "" : "s"}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono">{u.dog_count}</td>
                  <td className="p-3 text-xs text-[#6B6B6B]">{formatDate(u.created_at)}</td>
                  <td className="p-3 text-right">
                    {u.id === selfId ? (
                      <span className="text-[11px] text-[#6B6B6B] italic">you</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDetailTargetId(u.id)}
                          className="h-7 rounded-full border-[#E5E5E5] text-xs"
                          data-testid={`admin-user-details-${u.id}`}
                          title="Reset password, grant comp plans, view payments"
                        >
                          <IdentificationCard size={11} className="mr-1" />
                          Manage
                        </Button>
                        <a
                          href={`${API}/admin/users/${u.id}/audit-packet.pdf?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-7 px-2.5 rounded-full border border-[#E5E5E5] text-xs hover:bg-[#FBF7EC] hover:border-[#C9A227] text-ink"
                          data-testid={`admin-user-audit-packet-${u.id}`}
                          title="Download a defensible PDF of every consent, contract, deposit, and venue acknowledgement for this user"
                        >
                          <FileArrowDown size={11} className="mr-1" />
                          Audit&nbsp;packet
                        </a>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 rounded-full border-[#E5E5E5] text-xs"
                              data-testid={`admin-user-toggle-role-${u.id}`}
                            >
                              <Crown size={11} className="mr-1" />
                              {u.role === "admin" ? "Demote" : "Promote"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {u.role === "admin" ? "Demote admin?" : "Promote to admin?"}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {u.role === "admin"
                                  ? `${u.email} will lose admin privileges.`
                                  : `${u.email} will gain full admin privileges — including user management and data export.`}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => toggleRole(u)}>
                                Confirm
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleSuspend(u)}
                          className={`h-7 rounded-full text-xs ${u.suspended ? "border-[#E5E5E5]" : "border-[#C1272D]/30 text-[#C1272D]"}`}
                          data-testid={`admin-user-toggle-suspend-${u.id}`}
                        >
                          {u.suspended ? <CheckCircle size={11} className="mr-1" /> : <ShieldSlash size={11} className="mr-1" />}
                          {u.suspended ? "Reinstate" : "Suspend"}
                        </Button>
                        {u.deletion_requested_at && !u.account_deleted && (
                          <Button
                            size="sm"
                            onClick={() => completeDeletion(u)}
                            className="h-7 rounded-full text-xs bg-[#C1272D] hover:bg-ink text-white"
                            data-testid={`admin-user-complete-deletion-${u.id}`}
                          >
                            Complete deletion
                          </Button>
                        )}
                        {!u.account_deleted && u.role !== "admin" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => purgeUser(u)}
                            className="h-7 rounded-full text-xs border-[#C1272D]/40 text-crimson hover:bg-[#C1272D]/5"
                            data-testid={`admin-user-purge-${u.id}`}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
          className="rounded-full"
          data-testid="admin-users-prev"
        >
          ← Previous
        </Button>
        <span className="text-xs text-[#6B6B6B] font-mono">
          {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
        </span>
        <Button
          variant="outline"
          disabled={offset + limit >= total}
          onClick={() => setOffset(offset + limit)}
          className="rounded-full"
          data-testid="admin-users-next"
        >
          Next →
        </Button>
      </div>

      <SuspendUserDialog
        open={!!suspendTarget}
        onClose={() => setSuspendTarget(null)}
        user={suspendTarget}
        onDone={load}
      />
      <AdminUserDetailsDialog
        userId={detailTargetId}
        open={!!detailTargetId}
        onClose={() => {
          setDetailTargetId(null);
          load();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Browse
// ---------------------------------------------------------------------------
function BrowsePanel() {
  const [collection, setCollection] = useState("dogs");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/admin/data/browse/${opts.collection || collection}`, {
        params: { q: q || undefined, limit, offset: opts.offset ?? offset },
      });
      setData(resp);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOffset(0);
    load({ collection, offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  useEffect(() => {
    load({ offset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const firstRow = data?.items?.[0];
  const columns = firstRow
    ? Object.keys(firstRow).filter((k) => typeof firstRow[k] !== "object" || firstRow[k] === null).slice(0, 7)
    : [];

  return (
    <div className="space-y-4" data-testid="admin-data-browse">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={collection}
          onChange={(e) => setCollection(e.target.value)}
          className="rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-sm"
          data-testid="admin-browse-collection"
        >
          {BROWSE_COLLECTIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <Input
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setOffset(0); load({ offset: 0 }); } }}
          className="max-w-sm rounded-lg"
          data-testid="admin-browse-search"
        />
        <Button
          onClick={() => { setOffset(0); load({ offset: 0 }); }}
          className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
        >
          Search
        </Button>
        {data && (
          <span className="ml-auto text-sm text-[#6B6B6B]">
            Total: <span className="font-mono">{data.total}</span>
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : !data || data.items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-10 text-center text-[#6B6B6B]">
          No records.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B]">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="text-left p-3 font-bold">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((row, idx) => (
                <tr key={row.id || idx} className="border-t border-[#E5E5E5] hover:bg-[#FAFAFA]">
                  {columns.map((c) => (
                    <td key={c} className="p-3 font-mono truncate max-w-[220px]">
                      {row[c] === null || row[c] === undefined ? (
                        <span className="text-[#6B6B6B]">—</span>
                      ) : typeof row[c] === "boolean" ? (
                        row[c] ? "true" : "false"
                      ) : (
                        String(row[c])
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            className="rounded-full"
          >
            ← Previous
          </Button>
          <span className="text-xs text-[#6B6B6B] font-mono">
            {data.total === 0 ? "0" : `${offset + 1}–${Math.min(offset + data.items.length, data.total)}`} of {data.total}
          </span>
          <Button
            variant="outline"
            disabled={offset + limit >= data.total}
            onClick={() => setOffset(offset + limit)}
            className="rounded-full"
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
function ExportPanel() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/data/collections")
      .then((r) => setCollections(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const downloadUrl = (name) =>
    `${API}/admin/data/export/${name}.csv?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;

  const openInSheets = async (name) => {
    const { openInGoogleSheets } = await import("@/lib/google-sheets");
    await openInGoogleSheets({
      api,
      endpoint: `/admin/data/export/${name}.csv`,
      filenameHint: `${name}.csv`,
    }).catch(() => {});
  };

  return (
    <div className="space-y-4" data-testid="admin-data-export">
      <div className="bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4 text-sm text-[#8A6A00]">
        <strong>Heads up:</strong> exports contain full records from MongoDB. Treat them as
        confidential — they include email addresses, pedigree data, and payment metadata.
        <div className="mt-2 text-[12px] text-[#8A6A00]/85">
          Tip: each card opens a Google Sheets tab and copies the data to your clipboard — just paste
          into cell A1.
        </div>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {collections.map((c) => (
            <div
              key={c.name}
              className="bg-white border border-[#E5E5E5] rounded-xl p-5 hover:border-[#0E0E0E]/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all"
              data-testid={`admin-export-${c.name}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold capitalize">{c.name.replace(/_/g, " ")}</p>
                <DownloadSimple size={18} weight="duotone" className="text-gold" />
              </div>
              <p className="text-xs text-[#6B6B6B] font-mono mt-2">{c.count} records</p>
              <div className="mt-3 flex items-center gap-2">
                <a
                  href={downloadUrl(c.name)}
                  className="text-[12px] font-medium text-ink hover:underline"
                  data-testid={`admin-export-csv-${c.name}`}
                >
                  Download CSV ↓
                </a>
                <span className="text-[#D5D5D5]">·</span>
                <button
                  type="button"
                  onClick={() => openInSheets(c.name)}
                  className="text-[12px] font-medium text-emerald-700 hover:underline"
                  data-testid={`admin-export-gsheet-${c.name}`}
                >
                  Open in Sheets ↗
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
